import { useNavigate } from 'react-router-dom';
import {
  AlertTriangleIcon,
  BugIcon,
  BuildingIcon,
  DatabaseIcon,
  MailPlusIcon,
  ScanIcon,
  UserIcon,
  UsersIcon,
} from 'lucide-react';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import LoadingPage from '@/components/ui/LoadingPage';
import { useAdminOverview } from '@/hooks/useAdmin';
import { StatCard } from '../components/StatCard';
import { PipelineBar } from '../components/PipelineBar';
import { StatusBadge } from '../components/StatusBadge';
import { SectionCard } from '../components/SectionCard';
import { formatBytes, formatDayCount, formatNumber, formatRelative } from '../lib/format';
import { SEVERITY_STYLES } from '../components/issueStyles';
import { useAdminCaseSelection } from '../hooks/useAdminCaseSelection';

export function AdminOverview() {
  useDocumentTitle('Admin - Overview');
  const navigate = useNavigate();
  const { openCase } = useAdminCaseSelection();
  const { data, isLoading, isError } = useAdminOverview();

  if (isLoading) return <LoadingPage loadingText="Loading overview" />;
  if (isError || !data) {
    return <p className="p-6 text-sm text-white/60">Could not load the overview.</p>;
  }

  const { totals, pipeline, stuckCases, recentIssues } = data;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Clinics"
          value={formatNumber(totals.clinicCount)}
          hint={`${formatNumber(totals.orthodontistCount)} orthodontists`}
          icon={BuildingIcon}
          onClick={() => navigate('/admin/clinics')}
        />
        <StatCard
          label="Patients"
          value={formatNumber(totals.patientCount)}
          hint={`${formatNumber(totals.patientsLast30Days)} added in the last 30 days`}
          icon={UserIcon}
        />
        <StatCard
          label="Scans"
          value={formatNumber(totals.scanCount)}
          hint={`${formatNumber(totals.scansLast30Days)} in the last 30 days`}
          icon={ScanIcon}
          onClick={() => navigate('/admin/pipeline')}
        />
        <StatCard
          label="Users"
          value={formatNumber(totals.userCount)}
          hint={
            totals.disabledUserCount > 0
              ? `${formatNumber(totals.disabledUserCount)} disabled`
              : 'all active'
          }
          icon={UsersIcon}
          onClick={() => navigate('/admin/people')}
        />
        <StatCard
          label="Open issues"
          value={formatNumber(totals.openIssueCount)}
          hint="Reported from the app"
          icon={BugIcon}
          tone={totals.openIssueCount > 0 ? 'warning' : 'neutral'}
          onClick={() => navigate('/admin/issues')}
        />
        <StatCard
          label="Pending invitations"
          value={formatNumber(totals.pendingInvitationCount)}
          hint="Links not yet redeemed"
          icon={MailPlusIcon}
          onClick={() => navigate('/admin/invitations')}
        />
        <StatCard
          label="Scan meshes"
          value={formatBytes(totals.scanMeshBytes)}
          hint="Stored inside Postgres"
          icon={DatabaseIcon}
        />
        <StatCard
          label="Database"
          value={formatBytes(totals.databaseBytes)}
          hint="Total on disk"
          icon={DatabaseIcon}
        />
      </div>

      <SectionCard
        title="Pipeline"
        description="Where cases are sitting across every clinic."
        action={{ label: 'Open board', onClick: () => navigate('/admin/pipeline') }}
      >
        <PipelineBar
          stages={pipeline}
          onSelect={(status) =>
            navigate(`/admin/pipeline${status ? `?status=${encodeURIComponent(status)}` : ''}`)
          }
        />
      </SectionCard>

      <div className="grid gap-4 xl:grid-cols-2">
        <SectionCard
          title="Waiting the longest"
          description="Open cases that have not moved stage for over a week."
          action={{ label: 'See all', onClick: () => navigate('/admin/pipeline?stuck=1') }}
        >
          {stuckCases.length === 0 ? (
            <p className="text-sm text-white/50">Nothing is stuck. Everything has moved recently.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-content-highlight/30">
              {stuckCases.map((row) => (
                <li key={row.scanId} className="flex flex-wrap items-center gap-3 py-2.5">
                  <AlertTriangleIcon className="h-4 w-4 shrink-0 text-amber-300" />
                  <button
                    type="button"
                    onClick={() => openCase(row.patientId, row.scanId)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <span className="block truncate text-sm text-white">{row.patientName}</span>
                    <span className="block truncate text-xs text-white/45">{row.clinicName}</span>
                  </button>
                  <StatusBadge status={row.status} />
                  <span className="shrink-0 text-xs text-white/55">{formatDayCount(row.daysInStage)}</span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard
          title="Latest issue reports"
          description="Problems reported from inside the app."
          action={{ label: 'Open inbox', onClick: () => navigate('/admin/issues') }}
        >
          {recentIssues.length === 0 ? (
            <p className="text-sm text-white/50">No reports yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-content-highlight/30">
              {recentIssues.map((issue) => (
                <li key={issue.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/issues/${issue.id}`)}
                    className="flex w-full flex-wrap items-center gap-3 py-2.5 text-left"
                  >
                    <span className={SEVERITY_STYLES[issue.severity]}>{issue.severity}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-white">{issue.title}</span>
                      <span className="block truncate text-xs text-white/45">
                        {issue.clinicName ?? 'Unknown clinic'} · {formatRelative(issue.createdAt)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

export default AdminOverview;
